import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import Hero from "../components/Hero";
import { Section, SectionHeading, FadeIn } from "../components/Section";
import ServiceCard from "../components/ServiceCard";
import Testimonials from "../components/Testimonials";
import Marquee from "../components/Marquee";
import Button from "../components/Button";
import { SERVICES, STATS, VALUES } from "../data/content";

const Home = () => {
  return (
    <>
      <Hero />

      <Marquee />

      {/* About / Intro */}
      <Section spacing="py-24 md:py-36">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <FadeIn>
              <div className="overline text-primary mb-4">— About Fradro</div>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl lg:text-6xl leading-[1.02]">
                Empowering Nigeria's energy future.
              </h2>
            </FadeIn>
          </div>
          <div className="lg:col-span-7">
            <FadeIn delay={0.1}>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Through our subsidiaries — <strong className="text-foreground">Fradro International</strong> and{" "}
                <strong className="text-foreground">Fradro Gas &amp; Power</strong> — we deliver an integrated
                portfolio of services: from petroleum trading, shipping and
                logistics, to pipeline construction and sustainable natural gas
                supply. One accountable partner for every link of the energy
                chain.
              </p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {[
                  "World-class technical expertise",
                  "Visionary leadership team",
                  "Trusted global partnerships",
                  "Integrated energy solutions",
                ].map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-2 text-sm text-foreground/85"
                  >
                    <CheckCircle
                      weight="fill"
                      size={18}
                      className="text-primary mt-0.5 shrink-0"
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <Button to="/about" variant="outline" iconRight={ArrowUpRight}>
                  Read about us
                </Button>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Services Bento */}
      <Section
        className="bg-muted/40 border-y border-border"
        spacing="py-24 md:py-32"
      >
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <SectionHeading
              overline="Our services"
              title="Cutting-edge services across the energy value chain."
            />
          </div>
          <div className="lg:col-span-5 lg:pt-10">
            <FadeIn delay={0.1}>
              <p className="text-muted-foreground leading-relaxed">
                From advanced pipeline engineering to efficient petroleum
                trading, streamlined logistics and sustainable natural gas
                distribution — designed to empower industries and drive
                economic growth across Nigeria and Africa.
              </p>
              <Button
                to="/services"
                variant="ghost"
                className="mt-6 -ml-3"
                iconRight={ArrowUpRight}
              >
                See all services
              </Button>
            </FadeIn>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard
              key={s.title}
              index={i}
              number={s.number}
              title={s.title}
              description={s.short}
              Icon={s.Icon}
              image={s.image}
              className={i === 0 ? "lg:col-span-2 lg:row-span-1" : ""}
            />
          ))}
        </div>
      </Section>

      {/* Performance / Stats */}
      <Section spacing="py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <SectionHeading
              overline="Our performance"
              title="Landmark projects shaping the region."
              lead="Our flagship 153 km natural-gas transmission pipeline — running from Ogan/Agbor in Delta State to Awka in Anambra via Asaba, Onitsha and Nnewi — is a benchmark for engineering discipline and sustainable infrastructure in West Africa."
            />
          </div>
          <div className="lg:col-span-5">
            <FadeIn delay={0.1}>
              <img
                src="https://images.pexels.com/photos/36825977/pexels-photo-36825977.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Pipeline infrastructure"
                className="w-full h-72 object-cover border border-border"
                loading="lazy"
              />
            </FadeIn>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 border-t border-border">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`p-8 md:p-10 border-b border-border ${
                i % 2 !== 0 ? "border-l" : ""
              } lg:border-l ${i === 0 ? "lg:border-l-0" : ""}`}
            >
              <div className="font-display font-extrabold text-5xl md:text-6xl lg:text-7xl tracking-tight text-primary">
                {s.value}
              </div>
              <div className="overline text-muted-foreground mt-3">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Values */}
      <Section
        className="bg-card border-y border-border"
        spacing="py-24 md:py-32"
      >
        <SectionHeading
          overline="Why Fradro"
          title="Engineering excellence meets sustainable thinking."
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="p-8 border border-border bg-background hover:border-primary transition-all hover:-translate-y-1 group"
            >
              <div className="font-mono text-xs text-primary mb-4">
                0{i + 1}
              </div>
              <h3 className="font-display font-bold text-xl mb-3 group-hover:text-primary transition-colors">
                {v.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {v.description}
              </p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Testimonials />

      {/* Final CTA */}
      <Section spacing="py-24 md:py-32">
        <div className="border border-border bg-card p-10 md:p-16 lg:p-20 relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-60 pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="overline text-primary mb-4">— Contact us today</div>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl lg:text-6xl leading-[1.02]">
                Let's power the future, together.
              </h2>
              <p className="mt-6 max-w-2xl text-muted-foreground leading-relaxed">
                Cutting-edge technology, strategic partnerships and deep
                industry expertise — applied to the energy challenges that
                matter most to your business.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
              <Button
                to="/contact"
                size="lg"
                iconRight={ArrowUpRight}
                data-testid="home-final-cta-contact"
              >
                Contact us
              </Button>
              <Button to="/services" size="lg" variant="outline">
                View services
              </Button>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Home;
